/**
 * ZK Sync Panel Component
 *
 * Manages sync operations and displays sync history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Progress,
  Alert,
  Divider,
  Switch,
  Tooltip
} from 'antd';
import {
  SyncOutlined,
  CloudDownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { zkSyncApi, zkAttendanceApi, SyncStatus, ZkSyncHistory, RawAttendanceStats } from '../../services/zkService';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const ZKSyncPanel: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [rawStats, setRawStats] = useState<RawAttendanceStats | null>(null);
  const [syncHistory, setSyncHistory] = useState<ZkSyncHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Fetch status and history
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [status, stats, history] = await Promise.all([
        zkSyncApi.getStatus(),
        zkAttendanceApi.getStats(),
        zkSyncApi.getHistory({ limit: 20 })
      ]);
      setSyncStatus(status);
      setRawStats(stats);
      setSyncHistory(history);
    } catch (error) {
      console.error('Error fetching sync data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Poll for status updates during sync
    const interval = setInterval(() => {
      if (syncing || processing) {
        fetchData();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchData, syncing, processing]);

  // Sync all devices
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const result = await zkSyncApi.syncAll(false); // Don't auto-process, let user choose
      if (result.success) {
        message.success(`Synced ${result.totalRecordsNew} new records from ${result.successfulDevices}/${result.totalDevices} devices`);
      } else {
        message.error(`Sync failed: ${result.error}`);
      }
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Process to HR
  const handleProcessToHR = async () => {
    setProcessing(true);
    try {
      const result = await zkSyncApi.processToHR();
      message.success(
        `Processed ${result.processed} records: ${result.created} created, ${result.updated} updated` +
        (result.skipped > 0 ? `, ${result.skipped} skipped (unmapped)` : '')
      );
      if (result.errors.length > 0) {
        console.warn('Processing errors:', result.errors);
      }
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  // Sync history columns
  const historyColumns: ColumnsType<ZkSyncHistory> = [
    {
      title: 'Time',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: 'Type',
      dataIndex: 'syncType',
      key: 'syncType',
      width: 100,
      render: (type) => (
        <Tag color={type === 'manual' ? 'blue' : 'default'}>
          {type === 'manual' ? 'Manual' : 'Scheduled'}
        </Tag>
      )
    },
    {
      title: 'Device',
      key: 'device',
      width: 150,
      render: (_, record) => record.device?.deviceName || 'All Devices'
    },
    {
      title: 'Fetched',
      dataIndex: 'recordsFetched',
      key: 'recordsFetched',
      width: 80,
      align: 'center'
    },
    {
      title: 'New',
      dataIndex: 'recordsNew',
      key: 'recordsNew',
      width: 80,
      align: 'center',
      render: (count) => (
        <Text type={count > 0 ? 'success' : undefined}>{count}</Text>
      )
    },
    {
      title: 'Processed',
      dataIndex: 'recordsProcessed',
      key: 'recordsProcessed',
      width: 80,
      align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        switch (status) {
          case 'completed':
            return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
          case 'running':
            return <Tag icon={<LoadingOutlined spin />} color="processing">Running</Tag>;
          case 'error':
            return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
          default:
            return <Tag>{status}</Tag>;
        }
      }
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => {
        if (!record.endTime) return '-';
        const duration = dayjs(record.endTime).diff(dayjs(record.startTime), 'second');
        return `${duration}s`;
      }
    }
  ];

  // Progress component
  const SyncProgress = () => {
    if (!syncStatus?.progress || syncStatus.progress.status === 'idle') return null;

    const { progress } = syncStatus;

    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>
              {progress.status === 'syncing' ? 'Syncing...' :
                progress.status === 'processing' ? 'Processing...' :
                  progress.status === 'completed' ? 'Completed' : 'Error'}
            </Text>
            {progress.currentDevice && (
              <Text type="secondary">Current: {progress.currentDevice}</Text>
            )}
          </div>
          <Progress
            percent={Math.round((progress.processedDevices / progress.totalDevices) * 100) || 0}
            status={progress.status === 'error' ? 'exception' : progress.status === 'completed' ? 'success' : 'active'}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">Devices: {progress.processedDevices}/{progress.totalDevices}</Text>
            <Text type="secondary">Records: {progress.newRecords} new</Text>
          </div>
        </Space>
      </Card>
    );
  };

  return (
    <div>
      {/* Status Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Enabled Devices"
              value={syncStatus?.enabledDevices || 0}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Pending Records"
              value={rawStats?.pending || 0}
              valueStyle={{ color: (rawStats?.pending || 0) > 0 ? '#1890ff' : undefined }}
              prefix={<CloudDownloadOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Unmapped Records"
              value={rawStats?.unmapped || 0}
              valueStyle={{ color: (rawStats?.unmapped || 0) > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Today's Records"
              value={rawStats?.todayCount || 0}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Sync Info */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Space direction="vertical" size="small">
              <div>
                <Text type="secondary">Last Sync: </Text>
                <Text strong>
                  {syncStatus?.lastSync
                    ? dayjs(syncStatus.lastSync).format('YYYY-MM-DD HH:mm:ss')
                    : 'Never'}
                </Text>
                {syncStatus?.lastSync && (
                  <Text type="secondary"> ({dayjs(syncStatus.lastSync).fromNow()})</Text>
                )}
              </div>
              <div>
                <Text type="secondary">Next Scheduled: </Text>
                <Text>
                  {syncStatus?.nextScheduledSync
                    ? dayjs(syncStatus.nextScheduledSync).format('YYYY-MM-DD HH:mm:ss')
                    : 'Not scheduled'}
                </Text>
              </div>
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space size="middle">
              <Tooltip title="Fetch attendance records from all enabled devices">
                <Button
                  type="primary"
                  icon={<SyncOutlined spin={syncing} />}
                  onClick={handleSyncAll}
                  loading={syncing}
                  disabled={syncStatus?.isRunning}
                >
                  Sync Now
                </Button>
              </Tooltip>
              <Tooltip title="Process raw attendance data to HR attendance records (like Odoo's Synchronize HR Attendances)">
                <Button
                  type="primary"
                  ghost
                  icon={<CloudDownloadOutlined />}
                  onClick={handleProcessToHR}
                  loading={processing}
                  disabled={(rawStats?.pending || 0) === 0}
                >
                  Synchronize HR Attendances
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Progress */}
      <SyncProgress />

      {/* Warnings */}
      {(rawStats?.unmapped || 0) > 0 && (
        <Alert
          message={`${rawStats?.unmapped} attendance record(s) cannot be processed`}
          description="These records are from ZK users not mapped to ERP employees. Go to 'Employee Mapping' tab to fix this."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Sync History */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            Sync History
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        }
        size="small"
      >
        <Table
          columns={historyColumns}
          dataSource={syncHistory}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default ZKSyncPanel;
